import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Copy, Send, Loader2, MapPin } from "lucide-react";
import { CONTACTS, SERVICE_OPTIONS } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-paper border border-line rounded-xl px-4 py-3.5 font-body text-sm text-ink placeholder:text-ash transition-[border-color,box-shadow] duration-300";

const copyText = (text, label) => {
  navigator.clipboard.writeText(text).then(() => toast.success(`${label} copiato negli appunti`));
};

const RECAPITI = [
  { icon: Mail, label: "Email", value: CONTACTS.email, href: `mailto:${CONTACTS.email}`, testid: "contact-email-card" },
  { icon: Phone, label: "Telefono fisso", value: CONTACTS.phoneFixed, href: CONTACTS.phoneFixedHref, testid: "contact-phone-card" },
  { icon: MessageCircle, label: "Cellulare / WhatsApp", value: CONTACTS.mobile, href: CONTACTS.whatsapp, testid: "contact-whatsapp-card" },
];

const EMPTY = { name: "", email: "", phone: "", service: "", message: "" };

export const Contact = ({ prefill }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({ ...f, message: prefill }));
      toast.info("Richiesta pre-compilata dal simulatore: completa i tuoi dati");
    }
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service || "Richiesta generica",
        message: form.message,
      });
      setSent(true);
      setForm(EMPTY);
      toast.success("Messaggio inviato! Ti ricontatterò al più presto.");
    } catch (err) {
      toast.error("Invio non riuscito. Riprova o scrivimi direttamente via email o WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="relative py-20 sm:py-28 lg:py-36 grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="max-w-2xl mb-14"
        >
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-copper mb-5 flex items-center gap-3">
            <span className="w-8 h-px bg-copper inline-block" /> Contatti
          </p>
          <h2 className="font-display font-bold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-5xl text-ink">
            Un lavoro da fare? <span className="text-copper">Parliamone.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.08, ease: EASE }}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            {RECAPITI.map((r) => (
              <div
                key={r.label}
                data-testid={r.testid}
                className="group flex items-center gap-4 bg-paper border border-line rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(18,24,32,0.08)]"
              >
                <span className="w-12 h-12 rounded-xl bg-copper/10 flex items-center justify-center shrink-0">
                  <r.icon size={19} className="text-copper" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ash">{r.label}</p>
                  <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="font-display font-semibold text-lg text-ink hover:text-copper transition-colors duration-300 break-all">
                    {r.value}
                  </a>
                </div>
                <button
                  data-testid={`${r.testid}-copy-button`}
                  onClick={() => copyText(r.value, r.label)}
                  aria-label={`Copia ${r.label}`}
                  className="w-9 h-9 rounded-lg border border-line flex items-center justify-center text-ash hover:text-copper hover:border-copper transition-colors duration-300 shrink-0"
                >
                  <Copy size={14} />
                </button>
              </div>
            ))}

            <div className="mt-2 bg-night rounded-2xl p-6 text-paper">
              <div className="flex items-center gap-2.5 mb-3">
                <MapPin size={16} className="text-copper" />
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-paper/70">Area di copertura</span>
              </div>
              <p data-testid="contact-coverage-area" className="text-sm leading-relaxed text-paper/80">
                Empoli, Montelupo Fiorentino, Montespertoli, Vinci, Capraia e Limite, San Miniato, Castelfiorentino
                e comuni limitrofi. Assistenza da remoto in tutta Italia.
              </p>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.14, ease: EASE }}
            className="lg:col-span-7 bg-paper border border-line rounded-2xl p-6 sm:p-10 shadow-[0_20px_60px_rgba(18,24,32,0.06)]"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke mb-2.5">Nome e cognome *</label>
                <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Mario Rossi" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-email" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke mb-2.5">Email *</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="mario@esempio.it" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke mb-2.5">Telefono</label>
                <input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="333 0000000" className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-service" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke mb-2.5">Servizio di interesse</label>
                <select id="contact-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={`${inputCls} appearance-none cursor-pointer`}>
                  <option value="">Richiesta generica</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-message" className="block font-mono text-[11px] uppercase tracking-[0.18em] text-smoke mb-2.5">Messaggio *</label>
              <textarea
                id="contact-message"
                data-testid="contact-message-textarea"
                required
                minLength={10}
                rows={6}
                value={form.message}
                onChange={set("message")}
                placeholder="Descrivi il problema o il lavoro da fare…"
                className={`${inputCls} resize-y min-h-[140px]`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="mt-7 w-full sm:w-auto flex items-center justify-center gap-2.5 px-8 py-4 rounded-full bg-copper text-white font-display font-semibold text-sm hover:bg-copper-dark transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Invio in corso…" : "Invia la richiesta"}
            </button>
            {sent && (
              <p data-testid="contact-success-message" className="mt-4 font-mono text-xs text-leaf tracking-wide">
                Richiesta recapitata con successo — risposta rapida garantita.
              </p>
            )}
            <p className="mt-4 font-mono text-[11px] text-ash tracking-wide">
              Inviando accetti di essere ricontattato ai recapiti indicati. Nessuno spam, solo la risposta alla tua richiesta.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
